import React, { useEffect, useMemo, useState } from "react";
import { store } from "../../redux/oldStore.tsx";
import { toast } from "react-toastify";
import NhdNav from "../Layout/NhdNav";
import SiteEditor from "./SiteEditor";

/**
 * Port of /Contests — find NHD contests.
 * - Admin login in top nav
 * - Improved search
 * - When signed in: Login + Edit + Client Id (Mass Import removed)
 * - Email + Reset Cache admin buttons removed
 */
export default function Contests() {
  const [ready, setReady] = useState(false);
  const [clients, setClients] = useState([]);
  const [user, setUser] = useState(null);
  const [securityLevel, setSecurityLevel] = useState(0);
  const [search, setSearch] = useState("");
  const [editorClientId, setEditorClientId] = useState(null);

  const load = () => {
    store.server
      .getApi("Contests/List")
      .then((r) => {
        if (r && r.Success && r.Value) {
          setClients(r.Value.clients || []);
          setUser(r.Value.user || null);
          setSecurityLevel(r.Value.securityLevel || 0);
        } else {
          toast.error((r && r.Message) || "Could not load contests");
        }
        setReady(true);
      })
      .catch(() => {
        toast.error("Could not load contests");
        setReady(true);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const active = clients.filter((c) => c.active);
    if (!q) return active;
    return active.filter((c) => {
      const hay = [
        c.name,
        c.state,
        c.stateName,
        c.binding,
        String(c.fmsClientIntId || ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.indexOf(q) > -1;
    });
  }, [clients, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((c) => {
      const key = c.stateName || c.state || "Other";
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b))
      .map((stateName) => ({
        stateName,
        items: map[stateName].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        ),
      }));
  }, [filtered]);

  const inactive = useMemo(
    () => clients.filter((c) => !c.active),
    [clients]
  );

  const canAdminClient = (c) => {
    if (!user) return false;
    if (securityLevel > 50) return true;
    const ids = user.superClientIds || [];
    return ids.indexOf(c.fmsClientIntId) > -1;
  };

  const doAdminLogin = (c, goTo) => {
    store.server
      .postApi("Contests/GetClientSuperKey", { clientId: c.fmsClientId })
      .then((r) => {
        if (r && r.Success) {
          const key = r.Value || r.Data;
          let url = `https://${c.binding}/app/admin-login?x=${encodeURIComponent(
            key
          )}`;
          if (goTo) url += `&${goTo}`;
          const win = window.open(url, "_blank");
          if (!win) toast.warning("Please allow popups for this site");
        } else {
          toast.warning(
            (r && (r.Message || r.Data)) || "Login required or access denied"
          );
        }
      })
      .catch(() => toast.error("Admin login failed"));
  };

  const licenseDue = (c) => {
    if (!c.licenseValidThruUtc) return true;
    // ASP.NET JSON date or ISO
    let d = c.licenseValidThruUtc;
    if (typeof d === "string" && d.indexOf("/Date(") === 0) {
      const ms = parseInt(d.replace(/\D/g, ""), 10);
      d = new Date(ms);
    } else {
      d = new Date(d);
    }
    const limit = new Date();
    limit.setDate(limit.getDate() - 14);
    return d < limit;
  };

  return (
    <div className="nhd-page contests-page">
      <NhdNav user={user} onAuthChange={load} />
      <div className="nhd-page-body">
        <div className="contests-header flex-between wrap">
          <h1>Select Your History Day Contest</h1>
          <div className="contests-header-actions">
            {securityLevel > 100 && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setEditorClientId(0)}
              >
                <i className="fad fa-user-plus"></i> Add New Client
              </button>
            )}
          </div>
        </div>

        <div className="contests-search">
          <label htmlFor="contest-search">Search contests</label>
          <input
            id="contest-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by state, name, or site…"
            autoFocus
          />
          {ready && (
            <div className="contests-search-meta">
              Showing {filtered.length} of{" "}
              {clients.filter((c) => c.active).length} active contests
              {search ? ` matching “${search}”` : ""}
            </div>
          )}
        </div>

        {!ready && <div className="contests-loading">Loading contests…</div>}

        {ready && grouped.length === 0 && (
          <div className="contests-empty">
            No contests match your search. Try a different state or name.
          </div>
        )}

        {grouped.map((g) => (
          <div key={g.stateName} className="contest-state-group">
            <h2 className="contest-state-title">{g.stateName}</h2>
            <div className="contest-list">
              {g.items.map((c) => {
                // Hide empty-state rows for low-priv users (legacy behavior)
                if (
                  !c.stateName &&
                  securityLevel < 50 &&
                  !(c.state || "").trim()
                ) {
                  return null;
                }
                return (
                  <div key={c.id || c.fmsClientIntId} className="contest-row">
                    <div className="contest-row-main">
                      {c.binding ? (
                        <a
                          href={`https://${c.binding}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contest-name-link"
                        >
                          {c.name}
                        </a>
                      ) : (
                        <span className="contest-name">{c.name}</span>
                      )}
                      {user && licenseDue(c) && (
                        <span
                          className="text-danger"
                          title={`Payment is/was due. $${(
                            (c.renewalFeeInCents || 0) / 100
                          ).toFixed(2)}`}
                        >
                          <i className="fad fa-money-bill"></i>
                        </span>
                      )}
                    </div>
                    {user && (
                      <div className="contest-row-admin">
                        {canAdminClient(c) && (
                          <button
                            type="button"
                            className="btn-xs btn-warning"
                            onClick={() => doAdminLogin(c)}
                            title="Admin Login"
                          >
                            <i className="fad fa-user"></i> Login
                          </button>
                        )}
                        {securityLevel > 100 && (
                          <>
                            <button
                              type="button"
                              className="btn-xs btn-success"
                              onClick={() => setEditorClientId(c.id)}
                            >
                              Edit
                            </button>
                            <span className="client-id">
                              Client Id: {c.fmsClientIntId}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {user && inactive.length > 0 && (
          <div className="former-clients">
            <hr />
            <h3>Former Clients</h3>
            <ul>
              {inactive
                .slice()
                .sort((a, b) =>
                  `${a.stateName} ${a.name}`.localeCompare(
                    `${b.stateName} ${b.name}`
                  )
                )
                .map((c) => (
                  <li key={`inact-${c.id}`}>
                    {c.stateName || c.state} — {c.name}
                  </li>
                ))}
            </ul>
          </div>
        )}

        {user && (
          <div className="contests-stats">
            States:{" "}
            {
              new Set(
                clients.filter((c) => c.active).map((c) => c.stateName || c.state)
              ).size
            }{" "}
            · Clients: {clients.filter((c) => c.active).length}
          </div>
        )}
      </div>

      {editorClientId != null && (
        <SiteEditor
          clientId={editorClientId}
          onClose={() => setEditorClientId(null)}
          onSaved={() => {
            setEditorClientId(null);
            load();
          }}
        />
      )}
    </div>
  );
}
